import { FormProvider } from "react-hook-form";

import { Button, Inline, Stack, Text } from "@seneca/senekit";

import ControlledTextInput from "seneca-common/utils/form-hook/inputs/ControlledTextInput";
import {
  validateControlledEmailLoosely,
  validateControlledPassword
} from "seneca-common/utils/form-hook/utils/validationFunctions";
import { logSignUpEventByAccountType } from "seneca-common/utils/senecaAnalytics";

import { userAccountType } from "features/user";
import { ROUTES } from "routing/routes";

import {
  ErrorMessage,
  LogInLink,
  MarketingEmailsCheckbox,
  TermsAndConditionsCheckbox
} from "./form";
import { useSignUpEmailForm } from "./hooks";
import { SignUpCardContent } from "./Layout";
import { useLocationContext } from "./LocationProvider";

type Props = {
  email?: string;
};

const accountType = userAccountType.schoolparent;

export default function SignUpParentEmailForm({ email }: Props) {
  const { navigateInContext } = useLocationContext();

  const onSubmitSuccess = () => {
    logSignUpEventByAccountType(accountType);
    navigateInContext(ROUTES.SIGN_UP_PARENT.USER_DETAILS.buildPath({}), {
      replace: true
    });
  };

  const { form, handleSubmit, errorMessage } = useSignUpEmailForm({
    email,
    accountType,
    onSubmitSuccess
  });

  return (
    <FormProvider {...form}>
      <SignUpCardContent>
        {{
          body: (
            <Stack space="xlarge">
              {errorMessage && <ErrorMessage errorMessage={errorMessage} />}
              <Email email={email} />
              <Password autoFocus={!!email} />
              <TermsAndConditionsCheckbox />
              <MarketingEmailsCheckbox />
            </Stack>
          ),
          footer: (
            <Stack space="xxlarge">
              <SubmitButton
                loading={form.formState.isSubmitting}
                onClick={handleSubmit}
              />
              <LogInLink />
            </Stack>
          )
        }}
      </SignUpCardContent>
    </FormProvider>
  );
}

function Email({ email }: { email?: string }) {
  if (email) {
    return (
      <Inline space="small">
        <Text size="small">Create account for</Text>
        <Text size="small" weight="bold" dataNoTranslate>
          {email}
        </Text>
      </Inline>
    );
  }

  return (
    <ControlledTextInput
      name="email"
      label="Email"
      autoFocus
      validateObj={validateControlledEmailLoosely}
    />
  );
}

function Password({ autoFocus }: { autoFocus?: boolean }) {
  return (
    <ControlledTextInput
      name="password"
      label="Password"
      type="password"
      autoFocus={autoFocus}
      validateObj={validateControlledPassword}
    />
  );
}

function SubmitButton({
  loading,
  onClick
}: {
  loading: boolean;
  onClick: () => Promise<void>;
}) {
  return (
    <Button type="submit" width="100%" onClick={onClick} loading={loading}>
      Sign up
    </Button>
  );
}
